import type { Database, Json } from "@base-hormonal/database";
import type { TypedSupabaseClient } from "../../../shared/api/supabase";
import { fromSupabaseError, PublicAppError } from "../../../shared/lib/errors";
import { withRequestSignal } from "../../../shared/lib/request";
import { validatePlan, type PlanPayload } from "../model/plans";

type TrainingRow = Database["public"]["Tables"]["treinos"]["Row"];
type PhaseRow = Database["public"]["Tables"]["treino_fases"]["Row"];
type DayRow = Database["public"]["Tables"]["treino_dias"]["Row"];
type ExerciseRow = Database["public"]["Tables"]["treino_exercicios"]["Row"];

export interface Training extends TrainingRow {
  treino_fases: (PhaseRow & { treino_dias: (DayRow & { treino_exercicios: ExerciseRow[] })[] })[];
}

export const TRAINING_PAGE_SIZE = 20;

const TRAINING_COLUMNS =
  "id,aluno_id,user_id,nome,objetivo,restricoes,grupo_versao_id,versao,treino_anterior_id,status,data_inicio,data_fim_prevista,created_at,updated_at," +
  "treino_fases!treino_fases_treino_id_aluno_id_fkey(id,treino_id,nome,objetivo,duracao_semanas,ordem," +
  "treino_dias!treino_dias_fase_id_aluno_id_fkey(id,fase_id,nome,observacoes,ordem," +
  "treino_exercicios!treino_exercicios_dia_id_aluno_id_fkey(id,dia_id,exercicio_nome,series,repeticoes,carga,descanso_segundos,instrucoes,ordem)))";


function raise(error: { code?: string } | null): void {
  const safe = fromSupabaseError(error);
  if (safe !== null) throw safe;
}

/**
 * Escritor único de treino. O plano passa pelos RPCs transacionais
 * `salvar_treino_versionado` e `ativar_treino`: a versão anterior permanece
 * imutável e só um plano fica ativo por perfil.
 */
export function createTrainingApi(client: TypedSupabaseClient) {
  return {
    async plans(profileId: string, page = 0, signal?: AbortSignal): Promise<Training[]> {
      return withRequestSignal(signal, async (activeSignal) => {
        const { data, error } = await client
          .from("treinos")
          .select(TRAINING_COLUMNS)
          .eq("aluno_id", profileId)
          .order("created_at", { ascending: false })
          .range(page * TRAINING_PAGE_SIZE, (page + 1) * TRAINING_PAGE_SIZE - 1)
          .abortSignal(activeSignal);
        raise(error);
        return (data ?? []) as unknown as Training[];
      });
    },

    async savePlan(
      profileId: string,
      payload: PlanPayload,
      baseId: string | null,
    ): Promise<string> {
      const errors = validatePlan(payload);
      if (errors.length > 0) throw new PublicAppError("invalid_input", errors[0] ?? "");
      return withRequestSignal(undefined, async (signal) => {
        const { data, error } = await client
          .rpc("salvar_treino_versionado", {
            p_aluno_id: profileId,
            p_payload: payload as unknown as Json,
            ...(baseId === null ? {} : { p_treino_base_id: baseId }),
          })
          .abortSignal(signal);
        raise(error);
        return String(data ?? "");
      });
    },

    async activatePlan(planId: string): Promise<void> {
      await withRequestSignal(undefined, async (signal) => {
        const { error } = await client
          .rpc("ativar_treino", { p_treino_id: planId })
          .abortSignal(signal);
        raise(error);
      });
    },

    async deletePlan(planId: string): Promise<void> {
      await withRequestSignal(undefined, async (signal) => {
        const { error } = await client
          .from("treinos")
          .delete()
          .eq("id", planId)
          .abortSignal(signal);
        raise(error);
      });
    },

  };
}

export type TrainingApi = ReturnType<typeof createTrainingApi>;
